import { RTU } from '../../../types/RTU';
import { RTUNode, TreeNode } from '../../../types/TreeTypes';
import { isMissing } from '../../../utils/rtuDefaults';
import { ClusterInfo, orderWithClusters } from '../../RtuCluster/rtuCluster';

/** Display-only group heading; never written onto RTU leaf objects. */
export const UNASSIGNED_GROUP_LABEL = 'Unassigned';

const toGroupDisplayLabel = (value: string | null | undefined): string =>
  isMissing(value) ? UNASSIGNED_GROUP_LABEL : String(value);

/**
 * Same provider/field is the valid cluster case. A child whose own group differs from its master's is contradictory
 * data: it is not silently moved into the master's group; it stays an ordinary RTU in its own group.
 */
export const detachCrossGroupChildren = <T extends RTU>(rtus: T[]): T[] => {
  const byId = new Map<number, T>();
  rtus.forEach((rtu) => byId.set(rtu.id, rtu));
  return rtus.map((rtu) => {
    const master = rtu.clusterParentId === null || rtu.clusterParentId === undefined ? undefined : byId.get(rtu.clusterParentId);
    if (
      !master ||
      (toGroupDisplayLabel(master.provider) === toGroupDisplayLabel(rtu.provider) &&
        toGroupDisplayLabel(master.field) === toGroupDisplayLabel(rtu.field))
    ) {
      return rtu;
    }
    return { ...rtu, clusterParentId: null };
  });
};

/** JSON-encoded tuple so labels with colons or other separators cannot collide. */
const encodeGroupKey = (kind: 'provider' | 'field', ...parts: string[]): string =>
  JSON.stringify([kind, ...parts]);

export interface CreateTreeDataOptions {
  /**
   * Cluster placement of `inputRtus` when the caller already ordered them (see buildClusterView): the rows are used in
   * the given order, each child is grouped with its master. Omitted: placement is derived from clusterParentId.
   */
  info?: Record<number, ClusterInfo>;
}

/**
 * A child RTU is placed as a following row directly below its master, in the master's provider/field group
 * (a sibling leaf, not a nested node, so the existing TreeTable keyboard toggles are unaffected). A child whose
 * master is absent from `rtus` is an ordinary RTU in its own field.
 */
const createTreeData = (inputRtus: RTU[], options: CreateTreeDataOptions = {}): TreeNode[] => {
  const { rows: rtus, info } = options.info
    ? { rows: inputRtus, info: options.info }
    : orderWithClusters(inputRtus);
  const byId = new Map<number, RTU>();
  rtus.forEach((rtu) => byId.set(rtu.id, rtu));
  const placementOf = (rtu: RTU): RTU => {
    const masterId = info[rtu.id]?.masterId;
    return masterId !== null && masterId !== undefined ? byId.get(masterId) ?? rtu : rtu;
  };
  const groupByProvider = rtus.reduce((acc: Record<string, Record<string, RTU[]>>, rtu: RTU) => {
    const placement = placementOf(rtu);
    const providerLabel = toGroupDisplayLabel(placement.provider);
    const fieldLabel = toGroupDisplayLabel(placement.field);
    if (!acc[providerLabel]) {
      acc[providerLabel] = {};
    }
    if (!acc[providerLabel][fieldLabel]) {
      acc[providerLabel][fieldLabel] = [];
    }
    acc[providerLabel][fieldLabel].push(rtu);
    return acc;
  }, {});


  const treeData: TreeNode[] = Object.entries(groupByProvider).map(([provider, fields]) => {
    const providerNode: TreeNode = {
      key: encodeGroupKey('provider', provider),
      data: {
        id: '',
        name: '',
        client: '',
        location: '',
        latitude: '',
        longitude: '',
        status: '',
        configVersion: '',
        deploymentVersion: '',
        diskUsage: '',
        cpuUsage: '',
        lastData: '',
        installDate: '',
        publicIPAddress: '',
        localIPAddress: '',
        reportingToServer: null,
        lastConfigUpdate: null,
        groupId: '',
        systemRestart: false,
        provider: provider,
        canonicalStatus: null,
        statusObservedAtUtc: null,
        lastProbeAtUtc: null,
        lastFileReceivedAtUtc: null,
        statusSource: null,
        statusReason: null,
        onlineStatus: null,
      },
      children: Object.entries(fields).map(([field, groupedRtus]): TreeNode => {
        const fieldNode: TreeNode = {
          key: encodeGroupKey('field', provider, field),
          data:  {
            id: '',
            name: '',
            client: '',
            location: '',
            latitude: '',
            longitude: '',
            status: '',
            configVersion: '',
            deploymentVersion: '',
            diskUsage: '',
            cpuUsage: '',
            lastData: '',
            installDate: '',
            publicIPAddress: '',
            localIPAddress: '',
            reportingToServer: null,
            lastConfigUpdate: null,
            groupId: '',
            systemRestart: false,
            provider: field,
            canonicalStatus: null,
            statusObservedAtUtc: null,
            lastProbeAtUtc: null,
            lastFileReceivedAtUtc: null,
            statusSource: null,
            statusReason: null,
            onlineStatus: null,
          },
          children: groupedRtus.map((rtu): RTUNode => ({
            key: rtu.id.toString(),
            data: rtu
          }))
        };
        return fieldNode;
      })
    };
    return providerNode;
  });

  return treeData;
};

export default createTreeData;
